/**
 * Old console paths, and the paths that replace them.
 *
 * Bookmarks, Slack messages and tickets still name an old path. The root
 * route redirects that path to the new path. Page ids stay the same. The
 * backend API paths stay the same.
 */
export const MOVED_PAGES: Readonly<Record<string, string>> = {
  '/client-success/scorecard': '/business-performance/client-scorecard',
  '/client-success/payment-origin': '/business-performance/payment-origin',
  '/product/feature-adoption': '/business-performance/feature-adoption',
  '/reports/merchant-settings': '/merchants/merchant-settings',
  '/business-performance/onboarding-pipeline': '/merchants/onboarding-pipeline',
  '/reports/ai-decline-codes': '/payments/ai-decline-codes',
  '/payments/ach-returns': '/ach/returns',
  '/business-performance/ach-volume-trends': '/ach/volume-trends',
  '/business-performance/ach-nacha-rates': '/ach/nacha-rates',
  '/business-performance/ach-merchant-rejections': '/ach/merchant-rejections',
  '/business-performance/ach-return-reasons': '/ach/return-reasons',
  '/transaction-monitoring/ach-daily': '/ach/daily',
  '/transaction-monitoring/ach-trends': '/ach/trends',
  '/transaction-monitoring/ach-reason-codes': '/ach/reason-codes',
  '/chargebacks/overview': '/risk/chargebacks-overview',
  '/chargebacks/by-origin': '/risk/chargebacks-by-origin',
  '/business-performance/merchant-outliers': '/risk/merchant-outliers',
  '/business-performance/portfolio-risk-kpis': '/risk/portfolio-risk-kpis',
  '/cs-tools/collections-scorecard': '/collections/scorecard',
  '/cs-tools/account-delinquency': '/collections/account-delinquency',
  '/cs-tools/propensity-to-pay': '/collections/propensity-to-pay',
  '/cs-tools/propensity-accuracy': '/collections/propensity-accuracy',
  '/blytzcollect/analytics': '/collections/blytzcollect-analytics',
  '/reports/ai-texts': '/product/ai-texts',
  '/collections/ai-texts': '/product/ai-texts',
  '/product-tracking/ai-texts': '/product/ai-texts',
  '/reports/ai-decline-impact': '/collections/ai-decline-impact',
  '/reports/ai-decline-journeys': '/collections/ai-decline-journeys',
  '/platform/service-performance': '/engineering/service-performance'
}

/**
 * The new location for a pathname that moved, or nothing when it did not.
 *
 * A trailing slash matches the old path. The search and the hash are kept.
 * A path that is already new returns nothing, so the redirect does not repeat.
 *
 * @param pathname - The path, with no query and no hash.
 * @param search - The search string, with or without a leading `?`.
 * @param hash - The hash, with or without a leading `#`.
 * @returns The new location, or nothing when the path is not an old page.
 */
export function movedPageLocation(
  pathname: string,
  search = '',
  hash = ''
): string | undefined {
  const path =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname
  const next = MOVED_PAGES[path]
  if (next === undefined) return undefined
  const searchPart =
    search === '' || search.startsWith('?') ? search : `?${search}`
  const hashPart = hash === '' || hash.startsWith('#') ? hash : `#${hash}`
  return `${next}${searchPart}${hashPart}`
}
